import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { EMPTY } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { ApsPermitedScreen } from '../../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { Patterns } from '../../../models/constants/patterns';
import { ClientType } from '../../../models/enum/client-type';
import { getPriceNameList, PriceName } from '../../../models/enum/price-name';
import { CoverageCategory } from '../../../models/product/coverage-category';
import { PriceDto } from '../../../models/product/price-dto';
import { ProductPolicyRequestDto } from "../../../models/product/product-policy-request";
import { ProductPolicyResponseDto } from "../../../models/product/product-policy-response";
import { ProductVisit } from '../../../models/product/product-visit';
import { VinCategory } from '../../../models/vin/vin-category';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { CoverageService } from '../../../services/coverage/coverage.service';
import { PriceService } from '../../../services/price/price.service';
import { ProductPolicyService } from '../../../services/product-policy/product-policy.service';
import { ProductService } from '../../../services/product/product.service';
import { VinService } from '../../../services/vin/vin.service';

type ProductOption = {
  id: number;
  name: string;
};

type DropdownOption = {
  id: number;
  label: string;
};

type PriceDropdownOption = DropdownOption & {
  itemPriceUnit: string;
};

type DropdownChangeEvent<T> = {
  value: T;
};

export const ELIGIBILITY_COUNTRY_OPTIONS: { label: string; value: string }[] = [
  { label: 'TRANSLATE.CHOOSE.COUNTRY.ARG', value: ConstantsFordProtect.ARG },
  { label: 'TRANSLATE.CHOOSE.COUNTRY.BRA', value: ConstantsFordProtect.BRA },
  { label: 'TRANSLATE.CHOOSE.COUNTRY.CHL', value: ConstantsFordProtect.CHL },
  { label: 'TRANSLATE.CHOOSE.COUNTRY.COL', value: ConstantsFordProtect.COL },
  { label: 'TRANSLATE.CHOOSE.COUNTRY.VEN', value: ConstantsFordProtect.VEN }
];

@Component({
  selector: 'app-new-eligibility',
  templateUrl: './new-eligibility.component.html',
  styleUrls: ['./new-eligibility.component.scss'],
  standalone: false
})
export class NewEligibilityComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  listCountries = [...ELIGIBILITY_COUNTRY_OPTIONS];

  listSalesChannel = [
    { label: 'TRANSLATE.DEALER', value: ConstantsFordProtect.DEALER },
    { label: 'TRANSLATE.TELEMARKETING', value: ConstantsFordProtect.TELEMARKETING }
  ];

  listStatus = [
    { label: 'TRANSLATE.ACTIVE', value: ConstantsFordProtect.COVERAGE_ACTIVE },
    { label: 'TRANSLATE.INACTIVE', value: ConstantsFordProtect.COVERAGE_INACTIVE }
  ];

  listYearVehicle = [
    { label: 2017, value: 2017 },
    { label: 2018, value: 2018 },
    { label: 2019, value: 2019 },
    { label: 2020, value: 2020 },
    { label: 2021, value: 2021 },
    { label: 2022, value: 2022 },
    { label: 2023, value: 2023 },
    { label: 2024, value: 2024 },
    { label: 2025, value: 2025 },
    { label: 2026, value: 2026 },
    { label: 2027, value: 2027 }
  ];

  userform!: UntypedFormGroup;
  selectedProducts: ProductOption | null = null;
  products: ProductVisit[] = [];
  allProductArray: ProductOption[] = [];  // Backup for filtering
  idProduct: number | undefined;
  placeholderProduct = 'TRANSLATE.SELECT';

  selectedCountry: string | undefined | null;
  country: string | undefined | null;

  selectedCoverages: DropdownOption | null = null;
  coverages: CoverageCategory[] = [];
  coveragesConcat: DropdownOption[] = [];
  allCoveragesConcat: DropdownOption[] = [];  // Backup for filtering
  idCoverage: number | undefined;
  placeholderCoverage = 'TRANSLATE.SELECT';

  selectCanalVenda: string | undefined | null;
  selectedStatus: number | undefined | null;
  selectedYearVehicle: number | undefined | null;
  year: number | undefined | null;

  selectedVehicleLine: DropdownOption | null = null;
  vehicleLines: VinCategory[] = [];
  vehicleLineConcat: DropdownOption[] = [];
  allVehicleLineConcat: DropdownOption[] = [];  // Backup for filtering
  idVehicle: number | undefined;
  placeholderVehicle = 'TRANSLATE.SELECT';

  // Backup arrays for price field filtering (Phase 2)
  allWarrantyPricesConcat: PriceDropdownOption[] = [];
  allServiceCostPricesConcat: PriceDropdownOption[] = [];
  allFordCostPricesConcat: PriceDropdownOption[] = [];
  allSellerPricePricesConcat: PriceDropdownOption[] = [];
  allCustomersPricesConcat: PriceDropdownOption[] = [];

  selectedCustomerPrice: DropdownOption | null = null;
  selectedFordCostPrice: DropdownOption | null = null;
  selectedWarrantyCost: DropdownOption | null = null;
  selectedServiceCost: DropdownOption | null = null;
  selectedSellerPrice: DropdownOption | null = null;
  placeholdercustomerPrices = 'TRANSLATE.SELECT';
  placeholderField = 'TRANSLATE.SELECT';
  priceName: string = '';
  customerPriceName: number = 0;
  policyDescription: string = '';
  isSaving = false;
  submitted = false;
  isCtaSuccess = false;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  fordCostPriceName: number = 0;
  sellerPriceName: number = 0;
  warrantyPriceName: number = 0;
  serviceCostPriceName: number = 0;
  inputMonths: number = 0;
  isLoadingData = false;
  productPolicyRequestDto!: ProductPolicyRequestDto;
  productPolicyResponseDto!: ProductPolicyResponseDto;
  productArray: ProductOption[] = [];

  get customersPrices() { return this.pricesMap[PriceName.PRICE_CUSTOMER]; }
  get fordCost() { return this.pricesMap[PriceName.COST_FORD]; }
  get sellerPrices() { return this.pricesMap[PriceName.SELLER_PRICE]; }
  get warrantyCost() { return this.pricesMap[PriceName.COST_WARRANTY]; }
  get serviceCost() { return this.pricesMap[PriceName.COST_SERVICE]; }

  private filterPricesByCountry(prices: PriceDropdownOption[]): PriceDropdownOption[] {
    if (!prices || prices.length === 0) {
      return [];
    }

    // Brasil uses only R$, other countries use only $
    const isBrazil = this.selectedCountry === ConstantsFordProtect.BRA;
    const targetCurrency = isBrazil ? 'R$' : '$';

    return prices.filter(price => price.itemPriceUnit === targetCurrency);
  }

  get customersPricesConcat() { return this.filterPricesByCountry(this.concatMap[PriceName.PRICE_CUSTOMER]); }
  get fordCostPricesConcat() { return this.filterPricesByCountry(this.concatMap[PriceName.COST_FORD]); }
  get sellerPricePricesConcat() { return this.filterPricesByCountry(this.concatMap[PriceName.SELLER_PRICE]); }
  get warrantyPricesConcat() { return this.filterPricesByCountry(this.concatMap[PriceName.COST_WARRANTY]); }
  get serviceCostPricesConcat() { return this.filterPricesByCountry(this.concatMap[PriceName.COST_SERVICE]); }

  get isProductDisabled(): boolean {
    return !this.selectedCountry || this.isLoadingData;
  }

  get isCoverageDisabled(): boolean {
    return !this.idProduct || this.isLoadingData;
  }

  get isVehicleLineDisabled(): boolean {
    return !this.selectedCountry || !this.selectedYearVehicle || this.isLoadingData;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private apsPermissionService: ApsPermissionService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private productService: ProductService,
    private coverageService: CoverageService,
    private vinService: VinService,
    private priceService: PriceService,
    private productPolicyService: ProductPolicyService,
    private router: Router
  ) { }

  ngOnInit() {

    this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenViewsPlans);
    this.userform = this.fb.group({
      'selectCountry': [null, Validators.required],
      'selectProduct': [{ value: null, disabled: true }, Validators.required],
      'selectCoverage': [{ value: null, disabled: true }, Validators.required],
      'selectCanalVendas': [null, Validators.required],
      'selectStatus': [null, Validators.required],
      'selectYearVehicles': [null, Validators.required],
      'selectVehicleLines': [{ value: null, disabled: true }, Validators.required],
      'inputMonthsOffer': ['', [Validators.required, Validators.min(1), Validators.max(999), Validators.pattern(Patterns.ONLY_NUMBERS)]],
      'selectedCustomerPrices': [null, Validators.required],
      'inputPolicyDescription': ['', [Validators.maxLength(50)]]
    });
    this.loadAllPrices();

    this.productPolicyRequestDto = new ProductPolicyRequestDto();
    this.productPolicyResponseDto = new ProductPolicyResponseDto();

    // Smart debounce with status tracking
    // Listen to form validity changes separately from value changes
    this.userform.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        // Immediately disable button if form becomes invalid
        if (status !== 'VALID') {
          clearTimeout(this.debounceTimer);
          this.isCtaSuccess = false;
        }
      });

    // Listen to value changes and debounce the enable
    this.userform.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // If form is not valid, skip debounce
        if (this.userform?.status !== 'VALID') {
          return;
        }

        // Form is valid - clear old timer and start new debounce
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          // Only enable if form is still valid after debounce
          if (this.userform?.status === 'VALID') {
            this.isCtaSuccess = true;
          }
        }, 1500);
      });
  }

  private checkFormCompletion(): void {
    this.isCtaSuccess = this.userform?.valid ?? false;
  }

  buttonReturn() {
    this.router.navigate(['view-plans'], { skipLocationChange: false, replaceUrl: false });
  }

  countryChange(event: DropdownChangeEvent<string | null>) {
    this.selectedCountry = event.value;
    this.country = this.selectedCountry;

    // Reset price selections when country changes (prices are filtered by currency)
    this.selectedCustomerPrice = null;
    this.selectedFordCostPrice = null;
    this.selectedWarrantyCost = null;
    this.selectedServiceCost = null;
    this.selectedSellerPrice = null;
    this.customerPriceName = 0;
    this.fordCostPriceName = 0;
    this.warrantyPriceName = 0;
    this.serviceCostPriceName = 0;
    this.sellerPriceName = 0;

    if (this.selectedCountry) {
      this.userform?.get('selectProduct')?.enable();
      this.getProducts(this.selectedCountry);
      if (this.selectedYearVehicle) {
        this.userform?.get('selectVehicleLines')?.enable();
      }
    } else {
      this.userform?.get('selectProduct')?.disable();
      this.userform?.get('selectProduct')?.reset();
      this.userform?.get('selectCoverage')?.disable();
      this.userform?.get('selectCoverage')?.reset();
      this.userform?.get('selectVehicleLines')?.disable();
      this.userform?.get('selectVehicleLines')?.reset();
      this.productArray = [];
      this.allProductArray = [];
      this.idProduct = undefined;
    }
  }

  getProducts(country: string) {
    this.isLoadingData = true;
    this.productService
      .getProductsByCountryForProductPolicies(country)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: ProductVisit[]) => {

        this.products = response;

        this.productArray = this.products.map(object => {
          return {
            id: object.productId,
            name: object.productName
          }
        });

        this.allProductArray = [...this.productArray];  // Backup for filtering

        if (this.products.length > 0) {
          this.placeholderProduct = 'TRANSLATE.SELECT';
        } else {
          this.placeholderProduct = 'TRANSLATE.NO.RESULT.FOUND';
        }
        this.isLoadingData = false;

      }, (err) => {
        this.isLoadingData = false;

        if (err.status === 400) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
          });

        } else if (err.status === 401) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
          });

        } else {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
          });
        }

      });

  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onProductSelect(event: any) {
    // Called when user selects an item from p-autoComplete suggestions
    const selectedProduct = event?.value;

    this.selectedProducts = selectedProduct;
    this.idProduct = selectedProduct?.id;

    if (this.idProduct !== undefined) {
      this.userform?.get('selectCoverage')?.enable();
      this.getSearchByProductId(this.idProduct);
    } else {
      this.userform?.get('selectCoverage')?.disable();
      this.userform?.get('selectCoverage')?.reset();
      this.coveragesConcat = [];
      this.allCoveragesConcat = [];
    }
  }

  productChange(event: DropdownChangeEvent<ProductOption | null>) {
    // Fallback handler for onChange event
    this.selectedProducts = event.value;
    this.idProduct = event.value?.id;

    if (this.idProduct !== undefined) {
      this.userform?.get('selectCoverage')?.enable();
      this.getSearchByProductId(this.idProduct);
    } else {
      this.userform?.get('selectCoverage')?.disable();
      this.userform?.get('selectCoverage')?.reset();
      this.coveragesConcat = [];
      this.allCoveragesConcat = [];
    }
  }

  getSearchByProductId(idProduct: number) {
    this.isLoadingData = true;
    this.coverageService
      .getSearchByProductId(idProduct)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: CoverageCategory[]) => {

        this.coverages = response;

        this.coveragesConcat = this.coverages.map(object => {
          return {
            label: `${object.visit} Visitas - ${object.term} Meses ${object.distance !== null ? ' - ' + object.distance + ' Km.' : '.'}`,
            id: object.id
          }
        });

        this.allCoveragesConcat = [...this.coveragesConcat];  // Backup for filtering

        this.placeholderCoverage = this.coveragesConcat.length > 0 ? 'TRANSLATE.SELECT' : 'TRANSLATE.NO.RESULT.FOUND';
        this.isLoadingData = false;

      }, (err) => {
        this.isLoadingData = false;

        if (err.status === 400) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.COVERAGE.NOT.FOUND')
          });
        } else if (err.status === 401) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
          });

        } else {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
          });
        }

      });

  }

  coverageChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedCoverages = event.value;
    this.idCoverage = event.value?.id;
  }

  canalVendasChange(event: DropdownChangeEvent<string | null>) {
    this.selectCanalVenda = event.value;
  }

  statusChange(event: DropdownChangeEvent<number | null>) {
    this.selectedStatus = event.value;
  }

  yearVehicleChange(event: DropdownChangeEvent<number | null>) {
    this.selectedYearVehicle = event.value;
    this.year = this.selectedYearVehicle;
    if (this.country && this.year) {
      this.userform?.get('selectVehicleLines')?.enable();
      this.getByCountryAndYear(this.country, this.year);
    } else {
      this.userform?.get('selectVehicleLines')?.disable();
      this.userform?.get('selectVehicleLines')?.reset();
      this.vehicleLineConcat = [];
      this.allVehicleLineConcat = [];
    }
  }

  getByCountryAndYear(country: string, year: number) {
    this.isLoadingData = true;
    this.vinService
      .getByCountryAndYear(country, year)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: VinCategory[]) => {

        this.vehicleLines = response;

        this.vehicleLineConcat = this.vehicleLines.map(object => {
          return {
            label: `${object.vehicleLine} - ${object.vehicleLineCode} - ${object.tmaCode}${object.versionCode !== null ? ' - ' + object.versionCode + '.' : '.'}`,
            id: object.idVinCategoryVersion
          }
        });

        this.allVehicleLineConcat = [...this.vehicleLineConcat];  // Backup for filtering

        this.placeholderVehicle = this.vehicleLineConcat.length > 0 ? 'TRANSLATE.SELECT' : 'TRANSLATE.NO.RESULT.FOUND';
        this.isLoadingData = false;

      }, (err) => {
        this.isLoadingData = false;

        if (err.status === 400) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.COVERAGE.NOT.FOUND')
          });
        }
        else if (err.status === 401) {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
          });

        } else {

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
          });
        }

      });
  }

  vehicleLineChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedVehicleLine = event.value;
    this.idVehicle = event.value?.id;
  }

  customerPriceChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedCustomerPrice = event.value;
    this.customerPriceName = event.value?.id ?? 0;
  }

  fordCostChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedFordCostPrice = event.value;
    this.fordCostPriceName = event.value?.id ?? 0;
  }

  warrantyCostChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedWarrantyCost = event.value;
    this.warrantyPriceName = event.value?.id ?? 0;
  }

  serviceCostChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedServiceCost = event.value;
    this.serviceCostPriceName = event.value?.id ?? 0;
  }

  sellerPriceChange(event: DropdownChangeEvent<DropdownOption | null>) {
    this.selectedSellerPrice = event.value;
    this.sellerPriceName = event.value?.id ?? 0;
  }

  formatProductIdName(item: string | Record<string, unknown> | null | undefined): string {
    // Safety check: return empty string for invalid inputs
    if (!item) {
      return '';
    }

    // If it's a string, return as-is
    if (typeof item === 'string') {
      return item;
    }

    // If it's not an object, return empty
    if (typeof item !== 'object') {
      return '';
    }

    // Extract id and name safely
    const idValue = item.id || item.productId;
    const nameValue = item.name || item.productName;

    const idText = idValue ? String(idValue).trim() : '';
    const nameText = nameValue ? String(nameValue).trim() : '';

    // Return combinations - avoid duplicates by checking for separator
    if (idText && nameText) {
      // Check if name already contains the id to avoid duplication
      if (nameText.includes(idText)) {
        return nameText;
      }
      return `${idText} - ${nameText}`;
    } else if (idText) {
      return idText;
    } else if (nameText) {
      return nameText;
    }

    return '';
  }

  searchProducts(event: AutoCompleteCompleteEvent) {
    // PrimeNG AutoComplete completeMethod for Product filtering
    const query = event.query || '';
    const queryLower = query.toLowerCase().trim();

    if (!queryLower) {
      this.productArray = [...this.allProductArray];
    } else {
      this.productArray = this.allProductArray.filter(product => {
        const idText = (product.id || '').toString().toLowerCase();
        const nameText = (product.name || '').toLowerCase();
        return idText.includes(queryLower) || nameText.includes(queryLower);
      });
    }
  }

  searchCoverages(event: AutoCompleteCompleteEvent) {
    // PrimeNG AutoComplete completeMethod for Coverage filtering
    const query = event.query || '';
    const queryLower = query.toLowerCase().trim();

    if (!queryLower) {
      this.coveragesConcat = [...this.allCoveragesConcat];
    } else {
      this.coveragesConcat = this.allCoveragesConcat.filter(coverage => {
        const labelText = (coverage.label || '').toLowerCase();
        return labelText.includes(queryLower);
      });
    }
  }

  searchVehicleLines(event: AutoCompleteCompleteEvent) {
    // PrimeNG AutoComplete completeMethod for Vehicle Line filtering
    const query = event.query || '';
    const queryLower = query.toLowerCase().trim();

    if (!queryLower) {
      this.vehicleLineConcat = [...this.allVehicleLineConcat];
    } else {
      this.vehicleLineConcat = this.allVehicleLineConcat.filter(vehicle => {
        const labelText = (vehicle.label || '').toLowerCase();
        return labelText.includes(queryLower);
      });
    }
  }

  // Phase 2: Search methods for price fields
  private normalizeForSearch(label: string, query: string): boolean {
    // Intelligent search that handles currency formatting
    // Allows user to search by: just numbers, currency symbol, or full formatted value

    const queryTrimmed = query.trim();

    // If empty query, show all
    if (!queryTrimmed) {
      return true;
    }

    const labelLower = label.toLowerCase();

    // Detect if user is searching by currency (starts with R$ or $)
    const queryStartsWithCurrency = queryTrimmed.startsWith('R$') || queryTrimmed.startsWith('$');
    const labelStartsWithCurrency = labelLower.startsWith('R$') || labelLower.startsWith('$');

    // If user specified a currency, only match that currency
    if (queryStartsWithCurrency) {
      if (!labelStartsWithCurrency) return false;
      // Both have currency, compare the formatted string
      return labelLower.includes(queryTrimmed.toLowerCase());
    }

    // If user is just typing numbers, extract numbers from both and compare
    const queryOnlyNumbers = queryTrimmed.replace(/[^\d,.]/g, '');
    const labelOnlyNumbers = label.replace(/[^\d,.]/g, '');

    // Normalize decimal separators (treat both . and , as decimal)
    const queryNormalized = queryOnlyNumbers.replace(/[,.]/g, '');
    const labelNormalized = labelOnlyNumbers.replace(/[,.]/g, '');

    // Match if query numbers are contained in label numbers
    return labelNormalized.includes(queryNormalized);
  }

  searchWarrantyCost(event: AutoCompleteCompleteEvent) {
    const query = event.query || '';

    if (!query.trim()) {
      this.concatMap[PriceName.COST_WARRANTY] = [...this.allWarrantyPricesConcat];
    } else {
      this.concatMap[PriceName.COST_WARRANTY] = this.allWarrantyPricesConcat.filter(item => {
        return this.normalizeForSearch(item.label || '', query);
      });
    }
  }

  searchServiceCost(event: AutoCompleteCompleteEvent) {
    const query = event.query || '';

    if (!query.trim()) {
      this.concatMap[PriceName.COST_SERVICE] = [...this.allServiceCostPricesConcat];
    } else {
      this.concatMap[PriceName.COST_SERVICE] = this.allServiceCostPricesConcat.filter(item => {
        return this.normalizeForSearch(item.label || '', query);
      });
    }
  }

  searchFordCost(event: AutoCompleteCompleteEvent) {
    const query = event.query || '';

    if (!query.trim()) {
      this.concatMap[PriceName.COST_FORD] = [...this.allFordCostPricesConcat];
    } else {
      this.concatMap[PriceName.COST_FORD] = this.allFordCostPricesConcat.filter(item => {
        return this.normalizeForSearch(item.label || '', query);
      });
    }
  }

  searchSellerPrice(event: AutoCompleteCompleteEvent) {
    const query = event.query || '';

    if (!query.trim()) {
      this.concatMap[PriceName.SELLER_PRICE] = [...this.allSellerPricePricesConcat];
    } else {
      this.concatMap[PriceName.SELLER_PRICE] = this.allSellerPricePricesConcat.filter(item => {
        return this.normalizeForSearch(item.label || '', query);
      });
    }
  }

  searchCustomerPrice(event: AutoCompleteCompleteEvent) {
    const query = event.query || '';

    if (!query.trim()) {
      this.concatMap[PriceName.PRICE_CUSTOMER] = [...this.allCustomersPricesConcat];
    } else {
      this.concatMap[PriceName.PRICE_CUSTOMER] = this.allCustomersPricesConcat.filter(item => {
        return this.normalizeForSearch(item.label || '', query);
      });
    }
  }

  contCaractereres() {
    const description = this.userform?.get('inputPolicyDescription')?.value;
    this.policyDescription = description || '';
    return this.policyDescription.length;
  }

  formatNumber(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const value = input.value;
    const valueFormat = value.replace(/[^0-9]/g, '');

    input.value = valueFormat;
    this.inputMonths = Number(valueFormat);
  }

  private focusFirstInvalidField(): void {
    const fieldOrder: { controlName: string; elementId: string }[] = [
      { controlName: 'selectCountry', elementId: 'country' },
      { controlName: 'selectProduct', elementId: 'productNew' },
      { controlName: 'selectCoverage', elementId: 'coverageNew' },
      { controlName: 'selectCanalVendas', elementId: 'canalVendasNew' },
      { controlName: 'selectStatus', elementId: 'statusNew' },
      { controlName: 'selectYearVehicles', elementId: 'yearVehicleNew' },
      { controlName: 'selectVehicleLines', elementId: 'vehicleNew' },
      { controlName: 'inputMonthsOffer', elementId: 'monthsOfferNew' }
    ];

    for (const field of fieldOrder) {
      const control = this.userform?.get(field.controlName);
      if (control && !control.disabled && (control.invalid || control.pending)) {
        this.focusFieldById(field.elementId);
        return;
      }
    }

    // Warranty Cost, Service Cost, and Ford Cost are now optional fields

    if (this.selectedSellerPrice === null || this.selectedSellerPrice === undefined || this.sellerPriceName <= 0) {
      this.focusFieldById('sellerPriceNew');
      return;
    }

    const customerPriceControl = this.userform?.get('selectedCustomerPrices');
    if (
      (customerPriceControl && !customerPriceControl.disabled && (customerPriceControl.invalid || customerPriceControl.pending)) ||
      this.customerPriceName <= 0
    ) {
      this.focusFieldById('customerPrice3New');
    }
  }

  private focusFieldById(elementId: string): void {
    setTimeout(() => {
      const host = document.getElementById(elementId) as HTMLElement | null;
      if (!host) {
        return;
      }

      const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-multiselect-label, [tabindex]') as HTMLElement | null;
      const target = focusableChild ?? host;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus();
    }, 0);
  }

  buttonSave() {
    if (this.isSaving) {
      return;
    }

    this.submitted = true;

    if (this.selectedCountry === undefined) {
      this.userform?.get('selectCountry')?.markAsTouched();
      this.selectedCountry = undefined;
    }

    if (this.products.length > 0 && this.selectedProducts === null) {
      this.userform?.get('selectProduct')?.markAsTouched();
      this.selectedProducts = null;
    }

    if (this.coveragesConcat.length > 0 && this.selectedCoverages === null) {
      this.userform?.get('selectCoverage')?.markAsTouched();
      this.selectedCoverages = null;
    }

    if (this.selectCanalVenda === null || this.selectCanalVenda === undefined) {
      this.userform?.get('selectCanalVendas')?.markAsTouched();
      this.selectCanalVenda = null;
    }

    if (this.selectedStatus === undefined || this.selectedStatus === null) {
      this.userform?.get('selectStatus')?.markAsTouched();
      this.selectedStatus = null;
    }

    if (this.selectedYearVehicle === undefined || this.selectedYearVehicle === null) {
      this.userform?.get('selectYearVehicles')?.markAsTouched();
      this.selectedYearVehicle = null;
    }

    if (
      (this.selectedYearVehicle !== null && this.selectedYearVehicle !== undefined) &&
      (this.selectedVehicleLine === undefined || this.selectedVehicleLine === null)
    ) {
      this.userform?.get('selectVehicleLines')?.markAsTouched();
      this.selectedVehicleLine = null;
    }

    if (this.userform?.get('inputMonthsOffer')?.invalid) {
      this.userform?.get('inputMonthsOffer')?.markAsPending();
    }

    if (this.selectedCustomerPrice === undefined || this.selectedCustomerPrice === null) {
      this.userform?.get('selectedCustomerPrices')?.markAsTouched();
      this.selectedCustomerPrice = null;
    }

    const hasSellerPriceError = this.selectedSellerPrice === null || this.selectedSellerPrice === undefined || this.sellerPriceName <= 0;
    const hasCustomerPriceError = this.selectedCustomerPrice === null || this.selectedCustomerPrice === undefined || this.customerPriceName <= 0;

    this.policyDescription = this.userform?.get('inputPolicyDescription')?.value || '';

    if (
      this.userform?.valid &&
      !hasSellerPriceError &&
      !hasCustomerPriceError
    ) {

      this.productPolicyRequestDto.country = this.country ?? '';
      this.productPolicyRequestDto.productId = this.idProduct ?? 0;
      this.productPolicyRequestDto.coverageCategoryId = this.idCoverage ?? 0;
      this.productPolicyRequestDto.saleType = parseInt(this.selectCanalVenda ?? '0', 10);
      this.productPolicyRequestDto.isActive = this.selectedStatus ?? 0;
      this.productPolicyRequestDto.vinCategoryVersionId = this.idVehicle ?? 0;
      this.productPolicyRequestDto.user = sessionStorage.getItem('userId') ?? '';
      this.productPolicyRequestDto.months = this.inputMonths;
      this.productPolicyRequestDto.clientType = ClientType.PF_PJ;
      this.productPolicyRequestDto.customerPriceId = this.customerPriceName;
      this.productPolicyRequestDto.fordCostPriceId = this.fordCostPriceName;
      this.productPolicyRequestDto.sellerPriceId = this.sellerPriceName;
      this.productPolicyRequestDto.warrantyPriceId = this.warrantyPriceName;
      this.productPolicyRequestDto.costServiceId = this.serviceCostPriceName;
      this.productPolicyRequestDto.policyDescription = this.policyDescription;

      this.isSaving = true;
      this.productPolicyService
        .save(this.productPolicyRequestDto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: ProductPolicyResponseDto) => {

            this.productPolicyResponseDto = response;

            this.isSaving = false;
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('TRANSLATE.SUCCESS'),
              detail: this.translateService.instant('TRANSLATE.SUCCESS.CREATE')
            });

            this.buttonReturn();

          },
          error: (err) => {
            this.isSaving = false;

            if (err.status === 400 || err.status === 404) {

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.NEW.ELIGIBILITY.ERROR')
              });
            } else if (err.status === 401) {

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
              });

            } else {

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
              });
            }

          }
        });

    } else {

      this.focusFirstInvalidField();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('TRANSLATE.ERROR'),
        detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
      });
    }

  }


  // Guarda los DTO que vienen del backend
  private pricesMap: Record<PriceName, PriceDto[]> = {
    [PriceName.PRICE_CUSTOMER]: [],
    [PriceName.COST_FORD]: [],
    [PriceName.SELLER_PRICE]: [],
    [PriceName.COST_WARRANTY]: [],
    [PriceName.COST_SERVICE]: []
  };

  // Guarda la versión {label,id,itemPriceUnit} para la UI
  private concatMap: Record<PriceName, PriceDropdownOption[]> = {
    [PriceName.PRICE_CUSTOMER]: [],
    [PriceName.COST_FORD]: [],
    [PriceName.SELLER_PRICE]: [],
    [PriceName.COST_WARRANTY]: [],
    [PriceName.COST_SERVICE]: []
  };

  private loadAllPrices(): void {

    this.priceService.getByCustomersPrices(getPriceNameList())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Esperamos que cada DTO traiga su priceName:
        map(dtos => this.groupByName(dtos)),   // Record<PriceName,PriceDto[]>

        tap(grouped => {
          // rellenamos los mapas
          Object.entries(grouped).forEach(([name, arr]) => {
            const key = name as PriceName;
            this.pricesMap[key] = arr;
            this.concatMap[key] = this.formatPrices(arr);

            // Save backup arrays for filtering (Phase 2)
            switch (key) {
              case PriceName.COST_WARRANTY:
                this.allWarrantyPricesConcat = [...this.concatMap[key]];
                break;
              case PriceName.COST_SERVICE:
                this.allServiceCostPricesConcat = [...this.concatMap[key]];
                break;
              case PriceName.COST_FORD:
                this.allFordCostPricesConcat = [...this.concatMap[key]];
                break;
              case PriceName.SELLER_PRICE:
                this.allSellerPricePricesConcat = [...this.concatMap[key]];
                break;
              case PriceName.PRICE_CUSTOMER:
                this.allCustomersPricesConcat = [...this.concatMap[key]];
                break;
            }
          });

          this.placeholdercustomerPrices =
            this.concatMap[PriceName.PRICE_CUSTOMER].length > 0
              ? 'TRANSLATE.SELECT'
              : 'TRANSLATE.NO.RESULT.FOUND';
        }),

        catchError(err => this.handlePriceError(err)),
        finalize(() => null)
      )
      .subscribe();
  }

  private groupByName(dtos: PriceDto[])
    : Record<PriceName, PriceDto[]> {

    return dtos.reduce((acc, dto) => {
      const key = dto.itemName as PriceName;  // backend debe enviar priceName
      (acc[key] = acc[key] || []).push(dto);
      return acc;
    }, {} as Record<PriceName, PriceDto[]>);
  }

  private formatPrices(prices: PriceDto[])
    : PriceDropdownOption[] {

    return prices.map(({ id, itemPrice, itemPriceUnit }) => ({
      id,
      label: this.formatCurrency(itemPrice, itemPriceUnit),
      itemPriceUnit
    }));
  }

  private formatCurrency(value: number, unit: string): string {
    switch (unit) {
      case 'R$':
        return value.toLocaleString('pt-BR',
          { style: 'currency', currency: 'BRL' });
      case '$':
        return value.toLocaleString('en-US',
          { style: 'currency', currency: 'USD' });
      default:
        return `${value} ${unit}`;
    }
  }

  private handlePriceError(err: HttpErrorResponse) {
    const t = this.translateService;

    const detailKey =
      err.status === 400 ? 'TRANSLATE.PRICE.NOT.FOUND' :
        err.status === 401 ? 'TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE' :
          'TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR';

    this.messageService.add({
      severity: 'error',
      summary: t.instant('TRANSLATE.ERROR'),
      detail: t.instant(detailKey)
    });

    return EMPTY;     // completa el stream sin propagar el error
  }
}